/* eslint-disable no-console */
import types from '../mutation-types';
import PipelineCardAPI from '../../api/pipelineCards';
import { handlePipelineError } from '../../routes/dashboard/pipeline/helpers/pipelineErrorHandler';
import LeadScoresAPI from '../../api/leadScores';
import DealStatusAPI from '../../api/pipeline/dealStatus';

// Cache duration for pipeline cards. Short TTLs cause the kanban board to
// re-fetch with limit:N every time the user navigates away and back,
// which can lose items from later pages. 5 minutes balances freshness
// with stability. Real-time WebSocket events update cards instantly anyway.
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Audit 2026-04-25 P0.7 — Map auxiliar para lookup O(1) por id.
// Substitui findIndex linear (custava O(n) com 2000+ items).
// Map global compartilhado entre mutations — não vai pro state reativo
// (Vue 3 não observa Maps profundamente; serve só pra acelerar lookups).
const itemsById = new Map();

const rebuildIndex = items => {
  itemsById.clear();
  items.forEach((item, idx) => itemsById.set(item.id, idx));
};

// setItemAt — cria um novo array com o item na posição idx substituído.
// Retorna o novo array; o chamador deve atribuir a _state.items.
//
// ANTES usava splice() in-place. O problema: watchers que observam a
// *referência* do array (ex: `() => props.pipelineCards` no PipelineTab)
// não disparam quando splice muta o array sem trocar a referência. Isso
// causava o bug "mudanças no Pipeline não aparecem em tempo real" —
// UPDATE_ITEM_STAGE, REPLACE_CARD, UPDATE_ITEM_DETAILS silenciosamente
// atualizavam o store mas o kanban não re-renderizava.
// Retornar um novo array garante que a referência muda e o watcher dispara.
const setItemAt = (items, idx, newItem) =>
  items.map((item, i) => (i === idx ? Object.freeze(newItem) : item));

// Audit 2026-05-07 — accountId vem da URL (rota), nunca de um getter que
// possa apontar pra valor stale durante transição de account.
const accountIdFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const segments = window.location.pathname.split('/');
  const idx = segments.indexOf('accounts');
  if (idx === -1) return null;
  const parsed = Number(segments[idx + 1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Ciclo 3b — chave do load de board em andamento (account|pipeline|filtros).
// Module-level (não reativo): serve só para dedupe/supersede de concorrência.
let inFlightBoardKey = null;

// Ciclo 3b (fix P0) — token de geração do board. Cada load REAL de board
// (fetchBoardByStages que passa dos guards de cache/dedupe) incrementa; cada
// fetchStageCards captura a geração no disparo e descarta a resposta se outra
// geração assumiu no meio do voo. Determinístico: NÃO depende do getter
// `pipelines/getSelectedPipeline` na hora do resolve — durante remount/
// transição de rota o getter passa por janela null/stale e descartava
// respostas VÁLIDAS do pipeline correto (colunas presas em "Carregando").
let boardGeneration = 0;

// Ciclo 3b — stages visíveis do board: oculta won/lost quando o pipeline
// desabilita as etapas de ganho/perdido (mesma regra do updateColumns no
// PipelineTab). Colunas ocultas não precisam de fetch.
const visibleStageIds = pipeline => {
  const stages = pipeline?.stages || {};
  const showWonLost = pipeline?.settings?.enable_won_lost_stages !== false;
  return Object.keys(stages).filter(id => {
    const stage = stages[id] || {};
    return showWonLost || (!stage.is_won_stage && !stage.is_lost_stage);
  });
};

// Ciclo 8 — ajuste OTIMISTA do badge de contagem da coluna. Um drag entre
// colunas (ou um move remoto) muda o card de stage no flat, mas
// meta.stage_counts (fonte do badge via backendStageCount em
// PipelineColumn.vue) só era atualizado no próximo fetch — o badge ficava
// defasado (QA ciclo 3b, cenário 6). Decrementa a origem / incrementa o
// destino aqui; o stage_counts do SERVIDOR continua autoritativo e reconcilia
// no próximo fetch (toda resposta de fetchStageCards re-emite o mapa inteiro).
// Filtros server-side ativos? stage_counts é filter-aware no servidor; um
// ajuste local cego inflaria/deflaria o badge com um card que pode nem casar
// o filtro (achado Codex ciclo 8) — nesse caso o ajuste otimista é pulado e
// o badge reconcilia no próximo fetch (comportamento pré-ciclo-8).
const hasActiveServerFilters = filters => {
  if (!filters) return false;
  return Object.values(filters).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') {
      return hasActiveServerFilters(value);
    }
    return (
      value !== null && value !== undefined && value !== '' && value !== false
    );
  });
};

const adjustStageCounts = (_state, fromStage, toStage) => {
  if (fromStage === toStage) return;
  if (hasActiveServerFilters(_state.activeFilters)) return;
  const counts = _state.meta?.stage_counts;
  // Sem baseline do servidor (mapa vazio) não há o que ajustar — o badge já
  // cai no fallback local (filteredItems.length na coluna).
  if (!counts || Object.keys(counts).length === 0) return;
  const next = { ...counts };
  if (fromStage && next[fromStage] !== undefined) {
    next[fromStage] = Math.max(0, next[fromStage] - 1);
  }
  if (toStage) {
    // O group(:pipeline_stage).count do backend omite stages zerados, então o
    // destino pode não ter chave — criar com 1 mantém o badge correto.
    next[toStage] = (next[toStage] || 0) + 1;
  }
  // Troca a referência do meta — contrato de reatividade dos computed que
  // observam store.state.pipelineCards.meta (ver setItemAt).
  _state.meta = { ..._state.meta, stage_counts: next };
};

const state = {
  isEnabled: false,
  items: [],
  meta: {
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false,
    stage_counts: {},
    next_cursor: null,
  },
  // Ciclo 3b — metadado de paginação POR STAGE (cursor keyset).
  // Estrutura: { [stageId]: { cursor, hasMore, loading } }.
  // Os ITEMS vivem só no array flat `items` (fonte de verdade única —
  // drag-and-drop, realtime e bulk operam nele); fetchStageCards alimenta o
  // flat via merge com dedupe e guarda aqui apenas o estado do cursor.
  cardsByStage: {},
  selectedItem: null,
  loading: false,
  // Audit 2026-04-25 — flag separada para "carregar mais" (scroll-driven append).
  // O `loading` aciona o skeleton em PipelineTab; usar ele para appends destrói
  // o DOM da kanban e perde a posição de scroll do usuário.
  appending: false,
  error: null,
  config: {
    title: window.globalConfig.PIPELINE_CARD_TITLE || 'Gestor de Pedidos',
    defaultView: 'pipelineCard',
    webhookUrl: '',
    webhookSecret: '',
    autoAssignment: false,
    notificationsEnabled: false,
    supportEmail: '',
    supportPhone: '',
    supportChatHours: '',
    supportChatEnabled: false,
  },
  lastUpdated: null,
  lastFetchedUserId: null, // Track user to invalidate cache on user switch
  lastFetchedPipelineId: null, // Track pipeline to invalidate cache on pipeline switch
  // Audit 2026-05-07 — invalida cache em troca de account (Vai Pri / ClickHype
  // bug: cards de account 3 vazavam para sessão da account 72 durante
  // transição de rota, gerando rajadas de 404 protetivos no console).
  lastFetchedAccountId: null,
  // NOOVI: filtros do board aplicados server-side (formato aninhado do
  // PipelineFilter.vue). Mudar os filtros invalida o cache via assinatura.
  activeFilters: {},
  lastFetchedFiltersSignature: null,
  // Ciclo 3b — modo do último load: 'board' (50/stage, parcial por design) ou
  // 'global' (lista completa). Um carimbo de board NUNCA pode satisfazer o
  // cache do fetch global (list/agenda renderizariam dados incompletos em
  // silêncio) e vice-versa (board sem cardsByStage perderia o load-more).
  lastFetchedMode: null,
  leadScoreDistribution: {
    hot: 0,
    warm: 0,
    cold: 0,
    total: 0,
    average: 0,
  },
};

const getters = {
  isPipelineCardEnabled: _state => _state.isEnabled,
  // P0.2 — sem spread (era [..._state.items]). Evita criar array novo de
  // 2000+ elementos a cada acesso. Componentes que precisam de cópia local
  // fazem spread por conta própria.
  getPipelineCardItems: _state => _state.items,
  getMeta: _state => _state.meta,
  // Ciclo 3b — metadado de paginação por stage: { [stageId]: { cursor,
  // hasMore, loading } }. Os items vivem em getPipelineCardItems (flat).
  getCardsByStage: _state => _state.cardsByStage,
  getSelectedItem: _state => _state.selectedItem,
  isLoading: _state => _state.loading,
  getError: _state => _state.error,
  getActiveFilters: _state => _state.activeFilters,
  getPipelineCardConfig: _state => _state.config,
  getPipelineCardTitle: _state =>
    window.globalConfig.PIPELINE_CARD_TITLE ||
    _state.config?.title ||
    'PipelineCard',
  getSupportEmail: _state => _state.config?.supportEmail || '',
  getSupportPhone: _state => _state.config?.supportPhone || '',
  getSupportChatHours: _state => _state.config?.supportChatHours || '',
  getSupportChatEnabled: _state => _state.config?.supportChatEnabled || false,
  // P0.7 — lookup O(1) via Map auxiliar (era find() linear sobre 2000+ items).
  getPipelineCardItemQualification: _state => itemId => {
    const idx = itemsById.get(itemId);
    const item = idx !== undefined ? _state.items[idx] : null;
    if (!item) {
      return null;
    }
    return {
      id: item.id,
      qualificationChecklist: item.qualification_checklist || {},
      qualificationScore: item.qualification_score || 0,
      leadScore: item.lead_score || 0,
      leadScoreFactors: item.lead_score_factors || {},
      leadScoreCategory: item.lead_score_category || 'cold',
    };
  },
};

const actions = {
  setPipelineCardEnabled({ commit }, isEnabled) {
    commit(types.SET_PIPELINE_CARD_ENABLED, isEnabled);
  },

  setSelectedItem({ commit }, item) {
    commit('SET_SELECTED_ITEM', item);
  },

  async updateItemOwner({ commit }, { itemId, ownerId }) {
    try {
      const response = await PipelineCardAPI.updateCard(itemId, {
        owner_id: ownerId,
      });
      commit('UPDATE_PIPELINE_CARD_ITEM', response.data);
      commit('SET_LAST_UPDATED');
      return response.data;
    } catch (error) {
      console.error('Failed to update item owner:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  // NOOVI: aplica os filtros do board (server-side). Guarda os filtros, força a
  // invalidação do cache e refetch — o backend retorna já filtrado, então as
  // colunas renderizam direto, sem filtragem client-side (que quebrava com a
  // paginação por stage).
  async setActiveFilters({ commit, dispatch }, filters = {}) {
    commit('SET_ACTIVE_FILTERS', filters || {});
    // Limpa a paginação por-stage (cardsByStage) — os cursores antigos não valem
    // mais com o conjunto filtrado.
    commit('RESET_CARDS_BY_STAGE');
    // Ciclo 3b — o board é o único consumidor de setActiveFilters; o refetch
    // pós-filtro segue o caminho por stage (paralelo, limit 50/coluna).
    return dispatch('fetchBoardByStages');
  },

  async fetchPipelineCardItems(
    { commit, dispatch, state: localState },
    { append = false } = {}
  ) {
    // Get current user and pipeline to validate cache
    const currentUserId = this.getters.getCurrentUserID;
    const currentPipeline = this.getters['pipelines/getSelectedPipeline'];
    const currentPipelineId = currentPipeline?.id;
    const currentAccountId = accountIdFromUrl();

    // NOOVI: assinatura dos filtros ativos — muda quando o usuário aplica/limpa
    // filtros, invalidando o cache para forçar um refetch server-side.
    const filtersSignature = JSON.stringify(localState.activeFilters || {});

    // Ciclo 7 — o append global usa CURSOR keyset (meta.next_cursor), não
    // offset: o flat pode conter merges do board (fetchStageCards), então
    // `items.length` não corresponde mais a um offset global confiável.
    // Pré-condições do append: o último load foi global (o cursor pertence a
    // uma página global) e o backend sinalizou que há mais.
    if (append) {
      if (
        localState.lastFetchedMode !== 'global' ||
        !localState.meta?.has_more ||
        !localState.meta?.next_cursor
      ) {
        return localState.items;
      }
    }

    // Skip cache check when appending (loading more)
    if (!append) {
      // Verifica se o cache é válido (including user, pipeline, account check)
      // Convert IDs to strings for consistent comparison
      const currentPipelineIdStr = currentPipelineId
        ? String(currentPipelineId)
        : null;
      const lastFetchedPipelineIdStr = localState.lastFetchedPipelineId
        ? String(localState.lastFetchedPipelineId)
        : null;

      const now = new Date().getTime();
      const isCacheValid =
        localState.items.length > 0 &&
        localState.lastUpdated &&
        localState.lastFetchedMode === 'global' && // Board stamp (50/stage) can't satisfy a full-list fetch
        localState.lastFetchedUserId === currentUserId && // Invalidate on user switch
        lastFetchedPipelineIdStr === currentPipelineIdStr && // Invalidate on pipeline switch
        localState.lastFetchedAccountId === currentAccountId && // Invalidate on account switch
        localState.lastFetchedFiltersSignature === filtersSignature && // Invalidate on filter change
        now - localState.lastUpdated < CACHE_DURATION_MS;

      if (isCacheValid) {
        // Retorna os dados cacheados sem mudar o estado de loading
        return localState.items;
      }
    }

    // Guard against duplicate concurrent calls — MAS uma mudança de filtro
    // sempre força o refetch (senão o filtro recém-aplicado seria descartado
    // silenciosamente quando há um fetch em andamento). Respostas stale de um
    // filtro antigo são descartadas no commit, abaixo.
    const filtersChanged =
      localState.lastFetchedFiltersSignature !== filtersSignature;
    // Key-aware (QA ciclo 7): trocar de pipeline com um load-more em voo
    // (appending=true) não pode suprimir o fetch do pipeline NOVO — a lista
    // dele ficava em "0 cards" até a próxima troca. Mesma lição do guard do
    // board (3b): mudança de pipeline/filtro SEMPRE passa.
    const pipelineChanged =
      String(localState.lastFetchedPipelineId || '') !==
      String(currentPipelineId || '');
    if (
      !filtersChanged &&
      !pipelineChanged &&
      (localState.loading || localState.appending)
    ) {
      return localState.items;
    }

    // Audit 2026-04-25 — append usa flag separada para não acionar o skeleton
    // (que destrói o DOM da kanban e perde scroll do usuário).
    if (append) {
      commit('SET_APPENDING', true);
    } else {
      commit('SET_LOADING', true);
    }
    try {
      if (!currentPipelineId) {
        throw new Error('Nenhum pipeline selecionado');
      }

      // Audit 2026-04-25 — backend limita a 500 por request (era 2000).
      // Ciclo 7 — "carregar mais" da LISTA pagina por cursor keyset global
      // (ordenação (position, id), mesmo contrato do board por coluna; o
      // backend emite meta.next_cursor também no caminho offset da página 1).
      // O board NÃO usa este caminho: pagina por coluna via fetchStageCards.
      const params = {
        limit: 500,
        filters: localState.activeFilters,
      };
      if (append) {
        params.cursor = localState.meta.next_cursor;
      } else {
        params.offset = 0;
      }
      const response = await PipelineCardAPI.getCards(
        currentPipelineId,
        params
      );

      // Handle paginated response (data + meta)
      const responseData = response.data;
      const items = responseData.data || responseData;
      const meta = responseData.meta || null;

      // Descarta resposta stale: se os filtros mudaram enquanto esta request
      // estava no ar, não sobrescreve o board com dados de um filtro antigo.
      if (JSON.stringify(localState.activeFilters || {}) !== filtersSignature) {
        return items;
      }

      // Ciclo 7 — append stale: o usuário trocou de pipeline enquanto o
      // "carregar mais" estava no ar. FAIL-CLOSED: exige o getter resolvendo
      // o MESMO pipeline; janela null (remount) também descarta — diferente
      // do board (3b), onde descartar prendia colunas, aqui o custo é só o
      // usuário clicar de novo, enquanto o fail-open re-carimbaria o cache
      // do pipeline antigo por cima do novo (achado Codex ciclo 7).
      if (append) {
        const pipelineIdNow = this.getters['pipelines/getSelectedPipeline']?.id;
        if (String(pipelineIdNow || '') !== String(currentPipelineId)) {
          return items;
        }
      }

      if (append) {
        // Merge, não append cego: ids já presentes no flat (board/realtime
        // inseriram cards de cursor posterior, ou card movido/reordenado
        // entre páginas) são ATUALIZADOS com os dados mais novos da resposta
        // em vez de descartados (achado Codex ciclo 7). Sem meta na resposta,
        // preserva o meta atual (nunca cair no SET_ — substituiria a lista).
        commit('MERGE_PIPELINE_CARD_ITEMS', items);
        commit('SET_META', meta || localState.meta);
      } else {
        commit('SET_PIPELINE_CARD_ITEMS', items);
        if (meta) {
          commit('SET_META', meta);
        }
      }
      // Sequências ativas vêm embutidas no payload do index (ciclo 3a) —
      // semeia o cache do badge e evita o N+1 de GET /cards/:id/sequences
      // que o onMounted de cada PipelineCard dispararia.
      dispatch('pipelineCardSequences/seedFromCards', items, { root: true });
      commit('SET_LAST_UPDATED');
      commit('SET_LAST_FETCHED_USER', currentUserId); // Track which user fetched
      commit('SET_LAST_FETCHED_PIPELINE', currentPipelineId); // Track which pipeline fetched
      commit('SET_LAST_FETCHED_ACCOUNT', currentAccountId); // Track which account fetched
      commit('SET_LAST_FETCHED_FILTERS', filtersSignature); // Track which filters fetched
      commit('SET_LAST_FETCHED_MODE', 'global');
      return items;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro em fetchPipelineCardItems:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      if (append) {
        commit('SET_APPENDING', false);
      } else {
        commit('SET_LOADING', false);
      }
    }
  },

  async createPipelineCardItem({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await PipelineCardAPI.createCard(payload);
      commit('ADD_PIPELINE_CARD_ITEM', response.data);
      commit('SET_LAST_UPDATED'); // Invalida cache ao criar
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Atualiza apenas o store local sem fazer chamada de API
  // Usado quando a API já foi chamada (ex: mudança de status)
  updateCardStageLocal({ commit }, { itemId, columnId }) {
    const numericItemId = parseInt(itemId, 10);
    commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
  },

  // Replace entire card in store (used when card is updated via API)
  replaceCard({ commit }, card) {
    commit('REPLACE_CARD', card);
  },

  async moveItemToStage(
    { commit, dispatch, state: localState },
    { itemId, columnId, lostReason = null, wonValue = null, wonNote = null }
  ) {
    // Garantir que itemId seja número
    const numericItemId = parseInt(itemId, 10);

    // Obter o item original para possível reversão — O(1) via Map
    const originalIdx = itemsById.get(numericItemId);
    const originalItem =
      originalIdx !== undefined ? localState.items[originalIdx] : undefined;

    // Snapshot do baseline ANTES do ajuste otimista: o ajuste reverso não é
    // simétrico quando a chave de origem não existia no mapa (o clamp/criação
    // de chave deixaria um count que o servidor nunca teve — achado Codex).
    const stageCountsSnapshot = localState.meta?.stage_counts
      ? { ...localState.meta.stage_counts }
      : null;

    if (originalItem) {
      // Modificar o item no store antes da API (igual à exclusão)
      commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
      // Ciclo 8 — badge da coluna acompanha o move otimista (decrementa a
      // origem, incrementa o destino); o fetch seguinte reconcilia.
      commit('ADJUST_STAGE_COUNTS', {
        fromStage: originalItem.pipeline_stage,
        toStage: columnId,
      });
    }

    try {
      // Chamar a API passando fromStage para evitar fetch redundante
      const fromStage = originalItem?.pipeline_stage;
      const response = await PipelineCardAPI.moveToStage(
        numericItemId,
        columnId,
        fromStage,
        {
          lostReason,
          wonValue,
          wonNote,
        }
      );
      // Atualizar o card completo no store com a resposta da API
      // moveToStage retorna o card diretamente (não o wrapper axios)
      if (response) {
        commit('REPLACE_CARD', response);
        // Backend pode ter ajustado o stage final (ex.: automação) — move o
        // delta do badge do destino solicitado para o efetivo.
        if (response.pipeline_stage && response.pipeline_stage !== columnId) {
          commit('ADJUST_STAGE_COUNTS', {
            fromStage: columnId,
            toStage: response.pipeline_stage,
          });
        }
      }
      // Invalidar cache do frontend após sucesso
      await dispatch('itemUpdated');
      return true;
    } catch (error) {
      // Em caso de erro, reverter para o estado original
      if (originalItem) {
        commit('UPDATE_ITEM_STAGE', {
          itemId: numericItemId,
          columnId: originalItem.pipeline_stage,
        });
        // Restaura o SNAPSHOT do baseline (não um ajuste reverso): quando a
        // chave de origem não existia, o reverso criaria um count que o
        // servidor nunca teve (achado Codex ciclo 8).
        if (stageCountsSnapshot) {
          commit('RESTORE_STAGE_COUNTS', stageCountsSnapshot);
        }
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async deletePipelineCardItem({ commit, state: localState }, itemId) {
    const originalItem = localState.items.find(i => i.id === itemId);
    commit('REMOVE_PIPELINE_CARD_ITEM', itemId);

    try {
      // Pass originalItem to avoid extra getCard call in API
      await PipelineCardAPI.deleteCard(itemId, { currentCard: originalItem });
      commit('SET_LAST_UPDATED'); // Invalida cache ao deletar
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_PIPELINE_CARD_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async updateItemStructure({ commit, dispatch }, { itemId, structure }) {
    commit('SET_LOADING', true);
    try {
      const response = await PipelineCardAPI.updateCard(itemId, {
        item_details: { item_structure: structure },
      });
      commit('UPDATE_ITEM_STRUCTURE', {
        itemId,
        structure: response.data.item_details?.item_structure,
      });
      await dispatch('itemUpdated'); // Invalida cache
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateItemDetails({ commit, state: localState }, { itemId, details }) {
    // Get current item from store (avoid extra API call)
    const currentItem = localState.items.find(i => i.id === itemId);

    // Optimistic update: update store first for faster UI
    commit('UPDATE_ITEM_DETAILS', { itemId, details });

    try {
      // Build payload using store data instead of fetching from API
      const payload = {
        pipeline_id: details.pipeline_id ?? currentItem?.pipeline_id,
        pipeline_stage: details.pipeline_stage ?? currentItem?.pipeline_stage,
        position: details.position ?? currentItem?.position,
        owner_id: details.owner_id ?? currentItem?.owner_id,
        item_details: details.item_details ?? currentItem?.item_details,
        conversation_display_id:
          details.conversation_display_id ??
          currentItem?.conversation_display_id,
        timer_started_at:
          details.timer_started_at ?? currentItem?.timer_started_at,
        timer_duration: details.timer_duration ?? currentItem?.timer_duration,
        custom_attributes:
          details.custom_attributes ?? currentItem?.custom_attributes,
      };

      // Pass currentItem to API for webhook change tracking
      const response = await PipelineCardAPI.updateCard(itemId, payload, {
        currentCard: currentItem,
      });
      return response.data;
    } catch (error) {
      // Revert optimistic update on error
      if (currentItem) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: currentItem });
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async registerActivity(
    { commit, dispatch, state: localState },
    { itemId, type, details }
  ) {
    try {
      // Get current item from store instead of API call
      const currentItem = localState.items.find(i => i.id === itemId);
      const currentItemDetails = currentItem?.item_details || {};

      const newActivity = {
        id: Date.now(),
        type,
        details,
        created_at: new Date().toISOString(),
        user: {
          id: this.getters.getCurrentUser.id,
          name: this.getters.getCurrentUser.name,
          avatar_url: this.getters.getCurrentUser.avatar_url,
        },
      };

      const activities = [
        ...(currentItemDetails.activities || []),
        newActivity,
      ];

      const payload = {
        item_details: {
          ...currentItemDetails,
          activities,
        },
      };

      const response = await PipelineCardAPI.updateCard(itemId, payload, {
        currentCard: currentItem,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async fetchPipelineCardConfig({ commit }) {
    // Config is loaded from globalConfig - no API call needed
    const config = {
      title: window.globalConfig.PIPELINE_CARD_TITLE || 'Pipeline',
      defaultView: 'pipelineCard',
      webhookUrl: '',
      webhookSecret: '',
      autoAssignment: false,
      notificationsEnabled: false,
      supportEmail: '',
      supportPhone: '',
      supportChatHours: '',
      supportChatEnabled: false,
    };

    commit('SET_PIPELINE_CARD_CONFIG', config);
    return config;
  },

  updateLocalItems({ commit }, items) {
    commit('SET_PIPELINE_CARD_ITEMS', items);
    commit('SET_LAST_UPDATED'); // Atualiza o timestamp ao receber dados externos
  },

  async recalculateLeadScore({ commit }, itemId) {
    try {
      const response = await LeadScoresAPI.recalculate(itemId);
      const item = response.data;
      const camelItem = {
        id: itemId,
        lead_score: item.lead_score,
        lead_score_factors: item.lead_score_factors,
        lead_score_category: item.lead_score_category,
        updated_at: item.updated_at,
      };
      commit('UPDATE_PIPELINE_CARD_ITEM', camelItem);
      return camelItem;
    } catch (error) {
      console.error('Failed to recalculate lead score:', error);
      throw error;
    }
  },

  async overrideLeadScore({ commit }, { itemId, score }) {
    try {
      const response = await LeadScoresAPI.override(itemId, score);
      const item = response.data;
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        lead_score: item.lead_score,
        manual_override: item.manual_override,
      });
      return item;
    } catch (error) {
      console.error('Failed to override lead score:', error);
      throw error;
    }
  },

  async fetchLeadScoreDistribution({ commit }) {
    try {
      const response = await LeadScoresAPI.getDistribution();
      commit('SET_LEAD_SCORE_DISTRIBUTION', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch lead score distribution:', error);
      throw error;
    }
  },

  async markAsWon({ commit, dispatch }, itemId) {
    try {
      const response = await DealStatusAPI.markAsWon(itemId);
      // Patch the board card with the authoritative payload — including
      // `status` and `pipeline_stage` so the card moves to the won column,
      // not just flips its won_at flag.
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        won_at: response.data.won_at,
        won_value: response.data.won_value,
        lost_at: null,
        lost_reason: null,
        status: response.data.status,
        pipeline_stage: response.data.pipeline_stage,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to mark deal as won:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async markAsLost({ commit, dispatch }, { itemId, reason }) {
    try {
      const response = await DealStatusAPI.markAsLost(itemId, reason);
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        lost_at: response.data.lost_at,
        lost_reason: response.data.lost_reason,
        won_at: null,
        status: response.data.status,
        pipeline_stage: response.data.pipeline_stage,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to mark deal as lost:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async reopenDeal({ commit, dispatch }, itemId) {
    try {
      const response = await DealStatusAPI.reopen(itemId);
      // reopen restores the card to its pre-won/pre-lost regular stage — patch
      // `pipeline_stage` so the board moves it out of the won/lost column.
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        won_at: null,
        lost_at: null,
        lost_reason: null,
        won_value: null,
        status: response.data.status,
        pipeline_stage: response.data.pipeline_stage,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to reopen deal:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  // Invalidate the board cache so the next fetchPipelineCardItems pulls
  // fresh data. Called by surfaces that mutate cards WITHOUT touching the
  // board's local `items` (e.g. PipelineSection in the conversation view,
  // BulkAddModal). Must null `lastUpdated` — stamping it to now() would
  // keep `isCacheValid` true and the board would stay stale until F5.
  async itemUpdated({ commit }) {
    commit('SET_LAST_UPDATED', null);
    return true;
  },

  // Reset cache on logout or user switch
  resetCache({ commit }) {
    // Fix P0 — invalida respostas de board/stage ainda em voo: nada do load
    // anterior ao reset pode mergear num store limpo.
    boardGeneration += 1;
    commit('SET_PIPELINE_CARD_ITEMS', []);
    commit('SET_META', {
      total: 0,
      limit: 50,
      offset: 0,
      has_more: false,
      stage_counts: {},
      next_cursor: null,
    });
    commit('CLEAR_STAGE_CARDS', null);
    commit('SET_LAST_UPDATED', null);
    commit('SET_LAST_FETCHED_USER', null);
    commit('SET_LAST_FETCHED_PIPELINE', null);
    commit('SET_LAST_FETCHED_ACCOUNT', null);
    commit('SET_ACTIVE_FILTERS', {});
    commit('SET_LAST_FETCHED_FILTERS', null);
    commit('SET_LAST_FETCHED_MODE', null);
  },

  // Audit 2026-05-07 — wipe full state when user switches between accounts.
  // Called by App.vue's watcher on `currentAccountId` change. Without this,
  // cards loaded for account A linger in `state.items` and components
  // mounted briefly during route transition fire requests with cardA IDs
  // against the new account's URL → backend returns 404 (protective) but
  // the console gets noisy.
  resetForAccountSwitch({ commit }) {
    // Fix P0 — mesma regra do resetCache: respostas em voo da conta anterior
    // nunca podem mergear no estado limpo da nova conta.
    boardGeneration += 1;
    commit('RESET_FOR_ACCOUNT_SWITCH');
  },

  // P1.1 / Ciclo 3b — fetch cards de UM stage específico com cursor pagination
  // keyset (position, id). Os ITEMS entram no array flat (`items`, fonte de
  // verdade única — drag-and-drop, realtime e bulk continuam intactos) via
  // merge com dedupe por id; `cardsByStage` guarda APENAS o metadado de
  // paginação do stage ({ cursor, hasMore, loading }).
  async fetchStageCards(
    { commit, dispatch, state: localState },
    { pipelineId, stageId, append = false, limit = 50, generation = null } = {}
  ) {
    if (!pipelineId || !stageId) {
      throw new Error('pipelineId e stageId são obrigatórios');
    }
    // Anti-stale por GERAÇÃO + assinatura de filtros. O default
    // `?? boardGeneration` cobre o "carregar mais" da coluna (append avulso):
    // um load de board novo incrementa a geração e invalida appends em voo.
    // Fix P0: NÃO usar o getter de pipeline selecionado aqui — durante
    // remount/transição de rota ele passa por janela null/stale e descartava
    // respostas válidas (colunas presas em "Carregando itens...").
    const gen = generation ?? boardGeneration;
    const filtersSignature = JSON.stringify(localState.activeFilters || {});
    commit('SET_STAGE_LOADING', { stageId, loading: true });

    try {
      const stageState = localState.cardsByStage[stageId];
      const cursor = append && stageState ? stageState.cursor : null;
      const params = {
        pipeline_id: pipelineId,
        pipeline_stage: stageId,
        limit,
        filters: localState.activeFilters,
      };
      if (cursor) params.cursor = cursor;

      const response = await PipelineCardAPI.getCards(pipelineId, params);
      const responseData = response.data;
      const items = responseData.data || responseData;
      const meta = responseData.meta || {};

      // Descarta resposta stale: outra geração de board assumiu (troca de
      // pipeline/conta, reset) ou os filtros mudaram no meio do voo.
      const isStale =
        gen !== boardGeneration ||
        JSON.stringify(localState.activeFilters || {}) !== filtersSignature;
      if (isStale) return items;

      commit('MERGE_PIPELINE_CARD_ITEMS', items);
      commit('SET_STAGE_PAGINATION', {
        stageId,
        cursor: meta.next_cursor || null,
        hasMore: !!meta.has_more,
      });
      // Sequências embutidas no payload (ciclo 3a) — evita o N+1 de
      // GET /cards/:id/sequences no onMounted de cada card.
      dispatch('pipelineCardSequences/seedFromCards', items, { root: true });

      // stage_counts filtrado vem em toda resposta — mantém o badge da coluna.
      if (meta.stage_counts) {
        commit('SET_META', {
          ...localState.meta,
          stage_counts: meta.stage_counts,
        });
      }
      return items;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      commit('SET_STAGE_LOADING', { stageId, loading: false });
    }
  },

  // Ciclo 3b — orquestradora do load do board (kanban): paginação server-side
  // real POR COLUNA. Substitui o fetch global de limit:500 (1,52s/446KB com
  // 400 cards e teto silencioso >500 cards). Dispara fetchStageCards em
  // PARALELO (limit 50/stage) somente para os stages VISÍVEIS do pipeline;
  // cada coluna pagina depois pelo próprio cursor (loadMoreFromAPI). O flat
  // continua sendo a fonte de verdade dos items.
  async fetchBoardByStages(
    { commit, dispatch, state: localState, rootGetters },
    { limit = 50 } = {}
  ) {
    const pipeline = rootGetters['pipelines/getSelectedPipeline'];
    const pipelineId = pipeline?.id;
    if (!pipelineId) return [];

    const currentUserId = rootGetters.getCurrentUserID;
    const currentAccountId = accountIdFromUrl();
    const filtersSignature = JSON.stringify(localState.activeFilters || {});

    // Cache de 5min com a MESMA chave do fetch global + modo 'board' — um
    // carimbo de board (50/stage) nunca satisfaz list/agenda e vice-versa
    // (um carimbo global não tem cardsByStage; o board precisa dos cursores).
    const now = new Date().getTime();
    const isCacheValid =
      localState.items.length > 0 &&
      localState.lastUpdated &&
      localState.lastFetchedMode === 'board' &&
      localState.lastFetchedUserId === currentUserId &&
      String(localState.lastFetchedPipelineId || '') === String(pipelineId) &&
      localState.lastFetchedAccountId === currentAccountId &&
      localState.lastFetchedFiltersSignature === filtersSignature &&
      now - localState.lastUpdated < CACHE_DURATION_MS;
    if (isCacheValid) return localState.items;

    // Guard de concorrência POR CHAVE: um load idêntico em andamento é
    // deduplicado, mas trocar pipeline/conta/filtro SUPERSEDE o load antigo
    // (sem isso, trocar para o pipeline B durante o load de A devolvia o
    // board de B vazio até outro trigger — achado Codex).
    const boardKey = `${currentAccountId}|${pipelineId}|${filtersSignature}`;
    if (localState.loading && inFlightBoardKey === boardKey) {
      return localState.items;
    }
    inFlightBoardKey = boardKey;

    // Fix P0 — nova geração de board: invalida respostas em voo do load
    // anterior (stage fetches E appends de coluna). Incrementa DEPOIS dos
    // early-returns de cache/dedupe — um dispatch deduplicado não pode
    // invalidar o load em andamento no qual ele se apoia.
    boardGeneration += 1;
    const generation = boardGeneration;

    commit('SET_LOADING', true);
    // Limpa erro de um load anterior — sucesso parcial/total deve renderizar.
    commit('SET_ERROR', null);
    try {
      // Board novo: limpa o flat e a paginação por stage do load anterior.
      commit('SET_PIPELINE_CARD_ITEMS', []);
      commit('RESET_CARDS_BY_STAGE');

      const stageIds = visibleStageIds(pipeline);
      const results = await Promise.allSettled(
        stageIds.map(stageId =>
          dispatch('fetchStageCards', {
            pipelineId,
            stageId,
            limit,
            generation,
          })
        )
      );

      // Anti-stale: outro load assumiu (troca de pipeline/conta — geração
      // mudou) ou os filtros mudaram no meio → NÃO carimba o cache. Crítico:
      // os fetchStageCards stale resolvem fulfilled (descartam o merge mas
      // não rejeitam), então sem o check de geração um load SUPERSEDED via
      // allSettled "0 falhas" carimbava o cache do próprio pipeline POR CIMA
      // do vencedor — voltar a ele dava cache hit com items de outro pipeline
      // e board vazio sem nenhuma request (QA re-teste, cenário 2).
      if (
        generation !== boardGeneration ||
        JSON.stringify(localState.activeFilters || {}) !== filtersSignature
      ) {
        return localState.items;
      }

      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length === 0) {
        // Carimba o cache só quando TODOS os stages resolveram — falha
        // parcial deixa o cache inválido para o próximo load tentar de novo.
        commit('SET_LAST_UPDATED');
        commit('SET_LAST_FETCHED_USER', currentUserId);
        commit('SET_LAST_FETCHED_PIPELINE', pipelineId);
        commit('SET_LAST_FETCHED_ACCOUNT', currentAccountId);
        commit('SET_LAST_FETCHED_FILTERS', filtersSignature);
        commit('SET_LAST_FETCHED_MODE', 'board');
      } else if (failures.length < results.length) {
        // Falha parcial: renderiza o que carregou (fetchStageCards setou o
        // erro do stage que falhou; sem limpar, o board inteiro viraria a
        // tela de erro mesmo com N-1 colunas válidas).
        commit('SET_ERROR', null);
      }
      return localState.items;
    } finally {
      // Um load superseded (outra chave assumiu) não pode apagar o loading
      // do load mais novo.
      if (inFlightBoardKey === boardKey) {
        inFlightBoardKey = null;
        commit('SET_LOADING', false);
      }
    }
  },

  async updateQualificationChecklist({ commit }, { itemId, checklist }) {
    try {
      const response = await PipelineCardAPI.updateQualificationChecklist(
        itemId,
        checklist
      );
      const item = response.data;
      commit('UPDATE_QUALIFICATION_CHECKLIST', {
        itemId,
        checklist: item.qualification_checklist,
        qualificationScore: item.qualification_score,
        leadScore: item.lead_score,
        leadScoreFactors: item.lead_score_factors,
      });
      commit('SET_LAST_UPDATED');
      return item;
    } catch (error) {
      console.error('Failed to update qualification checklist:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async recalculateLeadScoreFromChecklist({ commit }, itemId) {
    try {
      const response = await PipelineCardAPI.recalculateScore(itemId);
      const item = response.data;
      commit('UPDATE_LEAD_SCORE', {
        itemId,
        leadScore: item.lead_score,
        leadScoreFactors: item.lead_score_factors,
        leadScoreCategory: item.lead_score_category,
      });
      commit('SET_LAST_UPDATED');
      return item;
    } catch (error) {
      console.error('Failed to recalculate lead score:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },
};

const mutations = {
  [types.SET_PIPELINE_CARD_ENABLED](_state, isEnabled) {
    _state.isEnabled = isEnabled;
  },
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_APPENDING(_state, appending) {
    _state.appending = appending;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },
  SET_SELECTED_ITEM(_state, item) {
    _state.selectedItem = item;
  },
  // P0.2 — Object.freeze nos items individuais. Vue 3 detecta frozen e
  // pula a instalação de proxies reativos profundos. Para 2000+ cards com
  // item_details aninhado, isso elimina ~50-200ms de overhead na carga
  // inicial e em cada update reativo.
  // P0.7 — rebuilda o índice itemsById para lookup O(1) nas mutations.
  SET_PIPELINE_CARD_ITEMS(_state, items) {
    const frozen = items.map(item => Object.freeze(item));
    _state.items = frozen;
    rebuildIndex(frozen);
  },
  SET_META(_state, meta) {
    _state.meta = meta;
  },
  // Ciclo 3b — merge com dedupe por id no array flat (fonte de verdade dos
  // items do board). Item já existente é substituído (dados mais novos do
  // backend); novos são anexados ao fim. Mantém o índice O(1) consistente e
  // troca a referência do array (contrato dos watchers — ver setItemAt).
  MERGE_PIPELINE_CARD_ITEMS(_state, items) {
    if (!items || !items.length) return;
    let next = _state.items;
    const toAppend = [];
    items.forEach(item => {
      const idx = itemsById.get(item.id);
      if (idx === undefined) {
        toAppend.push(Object.freeze(item));
        itemsById.set(item.id, next.length + toAppend.length - 1);
      } else if (idx < next.length) {
        next = setItemAt(next, idx, item);
      } else {
        // Dupe dentro do MESMO batch (id repetido): substitui no pendente.
        toAppend[idx - next.length] = Object.freeze(item);
      }
    });
    _state.items = toAppend.length ? [...next, ...toAppend] : next;
  },
  // Ciclo 7: APPEND_PIPELINE_CARD_ITEMS removida — o append global da lista
  // usa MERGE_PIPELINE_CARD_ITEMS + SET_META (ids existentes são atualizados,
  // não descartados; achado Codex).
  ADD_PIPELINE_CARD_ITEM(_state, item) {
    const frozen = Object.freeze(item);
    _state.items = [..._state.items, frozen];
    itemsById.set(frozen.id, _state.items.length - 1);
    // Badge da coluna (meta.stage_counts → backendStageCount em
    // PipelineColumn.vue) é incrementado otimistamente p/ o stage do novo card.
    // Senão o contador ficava defasado até o próximo fetch (QA 2026-06-12: criar
    // card deixava 4 cards na coluna com o badge ainda em "3"). Os callers de
    // ADD são create (success) e o rollback de delete — em ambos o incremento é
    // correto; o drag usa UPDATE_ITEM_STAGE + ADJUST_STAGE_COUNTS (sem ADD), logo
    // não há dupla contagem. adjustStageCounts já pula sob filtros server-side.
    adjustStageCounts(_state, null, frozen.pipeline_stage);
  },
  REMOVE_PIPELINE_CARD_ITEM(_state, itemId) {
    const removed = _state.items.find(item => item.id === itemId);
    _state.items = _state.items.filter(item => item.id !== itemId);
    rebuildIndex(_state.items);
    // Decrementa o badge do stage do card removido (mesmo motivo do ADD; o
    // delete commitava REMOVE sem ajustar o contador → badge alto até o fetch).
    if (removed?.pipeline_stage) {
      adjustStageCounts(_state, removed.pipeline_stage, null);
    }
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    const numericItemId = parseInt(itemId, 10);
    const itemIndex = itemsById.get(numericItemId);

    if (itemIndex !== undefined && itemIndex !== -1) {
      const item = _state.items[itemIndex];
      _state.items = setItemAt(_state.items, itemIndex, {
        ...item,
        pipeline_stage: columnId,
      });
    }
  },
  // Ciclo 8 — ajuste otimista do badge da coluna (meta.stage_counts) quando
  // um card muda de stage no fluxo de drag (move otimista, revert em erro,
  // reconciliação pós-API). O servidor segue autoritativo: o próximo fetch
  // re-emite o mapa completo e corrige qualquer drift.
  ADJUST_STAGE_COUNTS(_state, { fromStage, toStage }) {
    adjustStageCounts(_state, fromStage, toStage);
  },
  // Restaura o baseline capturado antes de um ajuste otimista que falhou —
  // o ajuste reverso não é simétrico quando a chave de origem não existia.
  RESTORE_STAGE_COUNTS(_state, snapshot) {
    _state.meta = { ..._state.meta, stage_counts: { ...snapshot } };
  },
  REPLACE_CARD(_state, card) {
    const itemIndex = itemsById.get(card.id);
    if (itemIndex !== undefined && itemIndex !== -1) {
      _state.items = setItemAt(_state.items, itemIndex, card);
    }
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        item_structure: structure,
      });
    }
  },
  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const item = _state.items[index];
      const updatedFields = {};
      Object.keys(details).forEach(key => {
        if (key in item) {
          updatedFields[key] = details[key];
        } else if (key === 'item_details') {
          updatedFields.item_details = {
            ...item.item_details,
            ...details.item_details,
          };
        }
      });
      _state.items = setItemAt(_state.items, index, {
        ...item,
        ...updatedFields,
      });
    }
  },
  SET_PIPELINE_CARD_CONFIG(_state, config) {
    _state.config = config;
  },
  // Pass an explicit value (e.g. null) to set it directly; omit the
  // argument to stamp "now". Passing null marks the cache as stale so the
  // next fetchPipelineCardItems performs a real refetch (used by
  // itemUpdated / resetCache). Previously the argument was ignored and the
  // value was always stamped to now(), which kept the cache "valid" and
  // caused the board to show stale data until a full page reload.
  SET_LAST_UPDATED(_state, timestamp = undefined) {
    _state.lastUpdated =
      timestamp === undefined ? new Date().getTime() : timestamp;
  },

  SET_LAST_FETCHED_USER(_state, userId) {
    _state.lastFetchedUserId = userId;
  },

  SET_LAST_FETCHED_PIPELINE(_state, pipelineId) {
    _state.lastFetchedPipelineId = pipelineId;
  },

  SET_LAST_FETCHED_ACCOUNT(_state, accountId) {
    _state.lastFetchedAccountId = accountId;
  },

  SET_ACTIVE_FILTERS(_state, filters) {
    _state.activeFilters = filters || {};
  },

  SET_LAST_FETCHED_FILTERS(_state, signature) {
    _state.lastFetchedFiltersSignature = signature;
  },

  SET_LAST_FETCHED_MODE(_state, mode) {
    _state.lastFetchedMode = mode;
  },

  RESET_CARDS_BY_STAGE(_state) {
    _state.cardsByStage = {};
  },

  RESET_FOR_ACCOUNT_SWITCH(_state) {
    _state.items = [];
    _state.cardsByStage = {};
    _state.selectedItem = null;
    _state.meta = {
      total: 0,
      limit: 50,
      offset: 0,
      has_more: false,
      stage_counts: {},
      next_cursor: null,
    };
    _state.lastUpdated = null;
    _state.lastFetchedUserId = null;
    _state.lastFetchedPipelineId = null;
    _state.lastFetchedAccountId = null;
    // Limpa os filtros ativos — senão um filtro de label/agente/stage da conta
    // anterior vazaria para o board/export da próxima conta.
    _state.activeFilters = {};
    _state.lastFetchedFiltersSignature = null;
    _state.lastFetchedMode = null;
    _state.error = null;
    itemsById.clear();
  },

  UPDATE_PIPELINE_CARD_ITEM(_state, item) {
    const index = itemsById.get(item.id);
    if (index !== undefined && index !== -1) {
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...item,
      });
    }
  },

  SET_LEAD_SCORE_DISTRIBUTION(_state, distribution) {
    _state.leadScoreDistribution = distribution;
  },

  UPDATE_QUALIFICATION_CHECKLIST(
    _state,
    { itemId, checklist, qualificationScore, leadScore, leadScoreFactors }
  ) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const updatedFields = {
        qualification_checklist: checklist,
        qualification_score: qualificationScore,
      };
      if (leadScore !== undefined) {
        updatedFields.lead_score = leadScore;
      }
      if (leadScoreFactors !== undefined) {
        updatedFields.lead_score_factors = leadScoreFactors;
      }
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...updatedFields,
      });
    }
  },

  UPDATE_LEAD_SCORE(
    _state,
    { itemId, leadScore, leadScoreFactors, leadScoreCategory }
  ) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const updatedFields = { lead_score: leadScore };
      if (leadScoreFactors !== undefined) {
        updatedFields.lead_score_factors = leadScoreFactors;
      }
      if (leadScoreCategory !== undefined) {
        updatedFields.lead_score_category = leadScoreCategory;
      }
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...updatedFields,
      });
    }
  },

  // === REAL-TIME MUTATIONS (from WebSocket) ===

  // Add card from remote (other user created)
  ADD_PIPELINE_CARD_REMOTE(_state, card) {
    // Check if card already exists (dedupe)
    if (!itemsById.has(card.id) && card.id) {
      // Convert snake_case from server to match local format
      const normalizedCard = {
        id: card.id,
        pipeline_stage: card.stage || card.pipeline_stage,
        position: card.position,
        owner_id: card.owner_id,
        conversation_display_id: card.conversation_display_id,
        title: card.title,
        updated_at: card.updated_at,
        won_at: card.won_at,
        lost_at: card.lost_at,
        lead_score: card.lead_score,
        // Preserve any additional fields that might come from full card data
        ...card,
      };
      const frozen = Object.freeze(normalizedCard);
      _state.items = [..._state.items, frozen];
      itemsById.set(frozen.id, _state.items.length - 1);
    }
  },

  // Update card from remote (other user updated)
  UPDATE_PIPELINE_CARD_REMOTE(_state, { card }) {
    const index = itemsById.get(card.id);
    if (index !== undefined && index !== -1) {
      const existing = _state.items[index];
      // updated_at can arrive in TWO formats:
      //   - REST API (initial fetch): ISO 8601 string "2026-05-07T00:31:00.000Z"
      //   - WebSocket broadcast (BroadcastService): epoch SECONDS (number)
      // Coerce both to epoch ms for safe comparison. A naive `>=` between
      // a number and a string returns false, which silently dropped every
      // realtime update on the kanban board.
      const toEpochMs = v => {
        if (v == null) return 0;
        if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? 0 : ms;
      };
      const existingVersion = toEpochMs(existing.updated_at);
      const incomingVersion = toEpochMs(card.updated_at);

      // Only update if incoming version is newer or equal (to handle concurrent updates)
      if (incomingVersion >= existingVersion) {
        // Ciclo 8 — won/lost/updated remotos podem auto-mover o card de stage;
        // ajusta o badge da coluna junto (no-op quando o stage não muda).
        adjustStageCounts(
          _state,
          existing.pipeline_stage,
          card.stage || card.pipeline_stage || existing.pipeline_stage
        );
        // Normalize and merge — preserve local fields when broadcast payload is partial
        const normalizedCard = {
          ...existing,
          pipeline_stage:
            card.stage || card.pipeline_stage || existing.pipeline_stage,
          position: card.position ?? existing.position,
          owner_id: card.owner_id ?? existing.owner_id,
          title: card.title ?? existing.title,
          updated_at: incomingVersion,
          won_at: card.won_at !== undefined ? card.won_at : existing.won_at,
          lost_at: card.lost_at !== undefined ? card.lost_at : existing.lost_at,
          lost_reason: card.lost_reason ?? existing.lost_reason,
          lead_score: card.lead_score ?? existing.lead_score,
          deadline: card.deadline ?? existing.deadline,
          scheduled_at: card.scheduled_at ?? existing.scheduled_at,
          status: card.status ?? existing.status,
          priority: card.priority ?? existing.priority,
        };
        _state.items = setItemAt(_state.items, index, normalizedCard);
      }
    }
  },

  // Remove card from remote (other user deleted)
  REMOVE_PIPELINE_CARD_REMOTE(_state, cardId) {
    _state.items = _state.items.filter(item => item.id !== cardId);
    rebuildIndex(_state.items);
  },

  // Update card stage from remote (other user moved card)
  UPDATE_CARD_STAGE_REMOTE(_state, { cardId, stage, position, version }) {
    const index = itemsById.get(cardId);
    if (index !== undefined && index !== -1) {
      const existing = _state.items[index];
      // Same dual-format problem as UPDATE_PIPELINE_CARD_REMOTE — coerce both.
      const toMs = v => {
        if (v == null) return 0;
        if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? 0 : ms;
      };
      const existingVersion = toMs(existing.updated_at);
      const versionMs = toMs(version);

      // Only update if incoming version is newer
      if (versionMs >= existingVersion) {
        // Ciclo 8 — move remoto (outro usuário): badge da coluna acompanha.
        adjustStageCounts(_state, existing.pipeline_stage, stage);
        _state.items = setItemAt(_state.items, index, {
          ...existing,
          pipeline_stage: stage,
          position: position ?? existing.position,
          updated_at: version,
        });
      }
    }
  },

  // Reorder cards from remote (other user reordered)
  REORDER_CARDS_REMOTE(_state, { positions, version }) {
    if (!positions || !Array.isArray(positions)) return;

    // Acumular todas as trocas antes de reatribuir para evitar múltiplos
    // re-renders. Usar reduce para ir construindo o array novo a cada posição.
    let updatedItems = _state.items;
    positions.forEach(pos => {
      const index = itemsById.get(pos.id);
      if (index !== undefined && index !== -1) {
        const existing = updatedItems[index];
        updatedItems = setItemAt(updatedItems, index, {
          ...existing,
          position: pos.position,
          pipeline_stage:
            pos.pipeline_stage || pos.stage || existing.pipeline_stage,
          updated_at: version || existing.updated_at,
        });
      }
    });
    _state.items = updatedItems;
  },

  // Update labels for cards linked to a conversation
  UPDATE_CARD_LABELS_BY_CONVERSATION(
    _state,
    { conversationDisplayId, labels }
  ) {
    let changed = false;
    let updatedItems = _state.items;
    _state.items.forEach((item, index) => {
      if (item.conversation_display_id === conversationDisplayId) {
        const existing = updatedItems[index];
        // Update label_list in item_details.conversation
        const updatedItemDetails = {
          ...existing.item_details,
          conversation: {
            ...(existing.item_details?.conversation || {}),
            label_list: labels,
          },
        };
        updatedItems = setItemAt(updatedItems, index, {
          ...existing,
          item_details: updatedItemDetails,
        });
        changed = true;
      }
    });
    if (changed) {
      _state.items = updatedItems;
    }
  },

  // === P1.1 / Ciclo 3b — CURSOR PAGINATION BY STAGE ===
  // Metadado de paginação per-stage ({ cursor, hasMore, loading }); os items
  // vivem só no flat (MERGE_PIPELINE_CARD_ITEMS). Cursor keyset (28× mais
  // rápido que offset no worst-case 2.442-card stage).
  SET_STAGE_PAGINATION(_state, { stageId, cursor, hasMore }) {
    const existing = _state.cardsByStage[stageId] || { loading: false };
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: {
        ...existing,
        cursor: cursor || null,
        hasMore: hasMore || false,
      },
    };
  },
  SET_STAGE_LOADING(_state, { stageId, loading }) {
    const existing = _state.cardsByStage[stageId] || {
      cursor: null,
      hasMore: false,
    };
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: { ...existing, loading },
    };
  },
  CLEAR_STAGE_CARDS(_state, stageId) {
    if (stageId) {
      const next = { ..._state.cardsByStage };
      delete next[stageId];
      _state.cardsByStage = next;
    } else {
      _state.cardsByStage = {};
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
